import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { lazy, Suspense, useEffect } from "react";
import Index from "./pages/Index";
import CookieConsent from "./components/CookieConsent";
import ExamProjectBanner from "./components/ExamProjectBanner";

// Lazy load pages with preloading for smooth transitions
const KobSkins = lazy(() => import("./pages/KobSkins"));
const Auth = lazy(() => import("./pages/Auth"));
const Account = lazy(() => import("./pages/Account"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Success = lazy(() => import("./pages/Success"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Blogs = lazy(() => import("./pages/Blogs"));
const FAQ = lazy(() => import("./pages/FAQ"));
const SaelgSkins = lazy(() => import("./pages/SaelgSkins"));
const KontaktOs = lazy(() => import("./pages/KontaktOs"));
const Privatlivspolitik = lazy(() => import("./pages/Privatlivspolitik"));
const Handelsbetingelser = lazy(() => import("./pages/Handelsbetingelser"));
const TechStack = lazy(() => import("./pages/TechStack"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Preload main navigation pages immediately after app mounts
const preloadPages = () => {
  import("./pages/KobSkins");
  import("./pages/SaelgSkins");
  import("./pages/Blogs");
  import("./pages/FAQ");
  import("./pages/KontaktOs");
};

// Start preloading after a short delay to not block initial render
if (typeof window !== 'undefined') {
  setTimeout(preloadPages, 100);
}

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen bg-background animate-fade-in" />
);

const AnimatedRoutes = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Index />} />
        <Route path="/koeb-skins" element={<KobSkins />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/account" element={<Account />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/success" element={<Success />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/saelg-skins" element={<SaelgSkins />} />
        <Route path="/kontakt-os" element={<KontaktOs />} />
        <Route path="/privatlivspolitik" element={<Privatlivspolitik />} />
        <Route path="/handelsbetingelser" element={<Handelsbetingelser />} />
        <Route path="/tech-stack" element={<TechStack />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <CartProvider>
        <svg className="noise-overlay" aria-hidden="true">
          <filter id="noise">
            <feTurbulence 
              type="fractalNoise" 
              baseFrequency="0.65" 
              numOctaves="3" 
              stitchTiles="stitch"
            />
          </filter>
          <rect width="100%" height="100%" filter="url(#noise)" />
        </svg>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AnimatedRoutes />
          <ExamProjectBanner />
          <CookieConsent />
        </BrowserRouter>
      </CartProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
